import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ArrowLeft, Mail, KeyRound } from "lucide-react";

export default function Auth() {
  const { login, registerOrg, verifyCode } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initial = params.get("mode") === "org" ? "org" : "login";
  const [tab, setTab] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", org_name: "", join_code: params.get("code") || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [showCode, setShowCode] = useState(false);
  const [inviteCode, setInviteCode] = useState("");

  // passwordless state
  const [mode, setMode] = useState("code");           // "code" | "password"
  const [step, setStep] = useState("email");           // "email" | "verify"
  const [code, setCode] = useState("");

  const requestCode = async () => {
    if (!f.email) return toast.error("Enter your email");
    setLoading(true);
    try {
      await api.post("/auth/request-code", { email: f.email });
      setStep("verify");
      toast.success("We emailed you a 6-digit code");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  const submit = async () => {
    setLoading(true);
    try {
      if (tab === "login") {
        if (mode === "code") {
          if (step === "email") { await requestCode(); return; }
          await verifyCode(f.email, code);
          navigate("/app");
        } else {
          await login(f.email, f.password);
          navigate("/app");
        }
      } else if (tab === "org") {
        localStorage.setItem("mc_pending_wizard", "1");
        await registerOrg({ name: f.name, email: f.email, password: f.password, org_name: f.org_name });
        toast.success("Organization created! Let's set it up.");
        navigate("/wizard");
      }
    } catch (e) {
      localStorage.removeItem("mc_pending_wizard");
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const btnLabel = loading ? "Please wait…"
    : tab === "org" ? "Create organization"
    : mode === "code" ? (step === "email" ? "Email me a code" : "Verify & sign in")
    : "Sign in";

  const goToInvite = () => {
    const c = inviteCode.trim();
    if (!c) return toast.error("Enter your invite code");
    navigate(`/o/${encodeURIComponent(c)}`);
  };

  return (
    <div className="relative min-h-screen flex flex-col" style={{ paddingTop: "env(safe-area-inset-top)", backgroundColor: "#0d2340" }}>
      <img src="/landing-bg.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(13,35,64,0.55) 0%, rgba(13,35,64,0.8) 100%)" }} />
      <div className="relative z-10 px-5 py-4">
        <Button variant="ghost" size="sm" className="rounded-full text-white hover:bg-white/15 hover:text-white" data-testid="auth-back-btn" onClick={() => navigate("/")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
      </div>
      <div className="relative z-10 flex-1 flex items-center justify-center px-5 pb-10">
        <div className="w-full max-w-md bg-white rounded-3xl p-7 shadow-[0_8px_32px_rgba(44,44,62,0.08)] animate-fade-up">
          <div className="text-center mb-6">
            <img src="/icon-192.png" alt="Mission Companion" className="h-14 w-14 rounded-2xl mx-auto shadow-sm mb-3" />
            <h1 className="font-heading font-bold text-2xl text-[var(--brand-navy)]">Mission Companion</h1>
            <p className="text-sm text-muted-foreground">Sign in or create your mission space</p>
          </div>

          <Tabs value={tab} onValueChange={(v) => { setTab(v); setStep("email"); }}>
            <TabsList className="grid grid-cols-2 w-full rounded-full bg-muted p-1 mb-6">
              <TabsTrigger value="login" className="rounded-full text-xs" data-testid="tab-login">Sign in</TabsTrigger>
              <TabsTrigger value="org" className="rounded-full text-xs" data-testid="tab-org">New organization</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="space-y-4">
              {mode === "code" ? (
                step === "email" ? (
                  <>
                    <Field label="Email" testid="login-email" type="email" value={f.email} onChange={set("email")} placeholder="you@example.com" />
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" /> We'll email you a one-time code — no password needed.</p>
                  </>
                ) : (
                  <>
                    <div className="text-sm text-[#2C2C3E]">Enter the 6-digit code sent to <strong>{f.email}</strong></div>
                    <Input data-testid="login-code" inputMode="numeric" maxLength={6} value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      className="rounded-xl h-12 text-center text-2xl tracking-[0.4em] font-bold" placeholder="000000" />
                    <button type="button" data-testid="resend-code-btn" onClick={requestCode} className="text-xs accent-text hover:underline">Resend code</button>
                    <button type="button" onClick={() => { setStep("email"); setCode(""); }} className="text-xs text-muted-foreground hover:underline ml-3">Use a different email</button>
                  </>
                )
              ) : (
                <>
                  <Field label="Email" testid="login-email" type="email" value={f.email} onChange={set("email")} />
                  <Field label="Password" testid="login-password" type="password" value={f.password} onChange={set("password")} />
                  <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs accent-text hover:underline">Forgot password?</Link>
                </>
              )}
            </TabsContent>
            <TabsContent value="org" className="space-y-4">
              <Field label="Organization name" testid="org-name" value={f.org_name} onChange={set("org_name")} />
              <Field label="Your name" testid="org-leader-name" value={f.name} onChange={set("name")} />
              <Field label="Email" testid="org-email" type="email" value={f.email} onChange={set("email")} />
              <Field label="Password" testid="org-password" type="password" value={f.password} onChange={set("password")} />
            </TabsContent>
          </Tabs>

          <Button data-testid="auth-submit-btn" disabled={loading} onClick={submit}
            className="w-full mt-6 rounded-full accent-bg text-white h-12 hover:opacity-90">
            {btnLabel}
          </Button>

          {tab === "login" && (
            <button type="button" data-testid="toggle-login-mode"
              onClick={() => { setMode(mode === "code" ? "password" : "code"); setStep("email"); setCode(""); }}
              className="w-full mt-3 text-xs text-muted-foreground hover:text-[var(--brand-navy)] flex items-center justify-center gap-1">
              <KeyRound className="h-3 w-3" /> {mode === "code" ? "Sign in with a password instead" : "Sign in with an email code instead"}
            </button>
          )}

          {tab !== "org" && (
            <div className="mt-5 pt-5 border-t text-center">
              {!showCode ? (
                <button type="button" data-testid="have-invite-code-btn" onClick={() => setShowCode(true)} className="text-xs font-medium accent-text hover:underline">
                  Invited by a missionary? Enter your invite code
                </button>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Enter the invite code your missionary shared.</p>
                  <div className="flex gap-2">
                    <Input data-testid="invite-code-input" value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} placeholder="e.g. RESET" className="rounded-xl h-11 text-center tracking-widest font-semibold" />
                    <Button data-testid="invite-code-go-btn" onClick={goToInvite} className="rounded-full accent-bg text-white h-11 px-5 hover:opacity-90">Go</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <Label className="text-sm text-[#2C2C3E]">{label}</Label>
      <Input data-testid={testid} className="mt-1.5 rounded-xl h-11" {...props} />
    </div>
  );
}
